import React, { useState, useRef } from 'react';
import { ArrowLeftRight, Download, Upload, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLLECTION_LABELS = {
  chatbot_settings: 'Chatbot-Einstellungen',
  admin_ai_settings: 'Admin-KI',
  referral_settings: 'Empfehlungsprogramm',
  churn_risk_settings: 'Kündigungsrisiko',
  reminder_settings: 'Zahlungserinnerungen',
  winback_config: 'Win-Back Flow',
  google_review_settings: 'Google-Bewertungen',
  invoice_settings: 'Rechnungs-Einstellungen',
  automation_rules: 'Automations-Regeln',
  email_templates: 'E-Mail-Vorlagen',
};

export const SettingsSyncCard = () => {
  const [exportData, setExportData] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [applying, setApplying] = useState(false);
  const fileRef = useRef(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/settings/sync/export`, authHeaders());
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `einstellungen-export-${res.data.source_db}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export heruntergeladen');
    } catch (e) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const res = await axios.post(`${API}/settings/sync/compare`, parsed, authHeaders());
      setExportData(parsed);
      setCompareResult(res.data);
      const preselect = {};
      res.data.report.forEach((r) => { if (!r.equal) preselect[r.collection] = true; });
      setSelected(preselect);
      const diffs = res.data.report.filter((r) => !r.equal).length;
      toast.success(diffs ? `Vergleich fertig: ${diffs} Bereiche weichen ab` : 'Alles identisch ✅');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ungültige Export-Datei');
    } finally {
      e.target.value = '';
    }
  };

  const handleApply = async () => {
    const collections = Object.keys(selected).filter((k) => selected[k]);
    if (!collections.length) return toast.info('Keine Bereiche ausgewählt');
    if (!window.confirm(`${collections.length} Bereiche mit den importierten Werten überschreiben?`)) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/settings/sync/apply`, { export: exportData, collections }, authHeaders());
      toast.success(`Übernommen: ${Object.entries(res.data.applied).map(([k, v]) => `${COLLECTION_LABELS[k] || k} (${v})`).join(', ')}`);
      const cmp = await axios.post(`${API}/settings/sync/compare`, exportData, authHeaders());
      setCompareResult(cmp.data);
      setSelected({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Übernahme fehlgeschlagen');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card className="border-teal-200" data-testid="settings-sync-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowLeftRight className="text-teal-600" />
          Datenabgleich Preview ↔ Produktion
        </CardTitle>
        <CardDescription>
          Exportiere die Bot- & Automations-Einstellungen aus einer Umgebung als Datei,
          lade sie in der anderen Umgebung hoch, sieh genau was abweicht — und übernimm
          die Unterschiede per Klick. (Chatbot, Admin-KI, Empfehlungen, Mahnwesen, Automations-Regeln, E-Mail-Vorlagen u.a.)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="sync-export-btn">
            <Download className="h-4 w-4 mr-2" />
            Einstellungen exportieren (JSON)
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="sync-upload-btn">
            <Upload className="h-4 w-4 mr-2" />
            Export-Datei hochladen & vergleichen
          </Button>
          <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleFile} data-testid="sync-file-input" />
        </div>

        {compareResult && (
          <div className="space-y-3" data-testid="sync-compare-report">
            <p className="text-sm text-gray-600">
              Vergleich: <strong>{compareResult.source_db}</strong> (Export vom{' '}
              {new Date(compareResult.exported_at).toLocaleString('de-CH')}) → <strong>{compareResult.target_db}</strong> (diese Umgebung)
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {compareResult.report.map((r) => (
                <div key={r.collection} className={`p-3 rounded-lg border ${r.equal ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-200'}`} data-testid={`sync-row-${r.collection}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {!r.equal && (
                        <Checkbox
                          checked={!!selected[r.collection]}
                          onCheckedChange={(v) => setSelected({ ...selected, [r.collection]: !!v })}
                          data-testid={`sync-check-${r.collection}`}
                        />
                      )}
                      <p className="text-sm font-medium">{COLLECTION_LABELS[r.collection] || r.collection}</p>
                    </div>
                    <Badge variant="outline" className={r.equal ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {r.equal ? 'identisch' : 'weicht ab'}
                    </Badge>
                  </div>
                  {!r.equal && r.type === 'singleton' && (
                    <div className="mt-2 space-y-1">
                      {r.changes.map((c) => (
                        <p key={c.field} className="text-xs text-gray-700">
                          <span className="font-mono font-medium">{c.field}</span>: „{String(c.local)}" → „{String(c.imported)}"
                        </p>
                      ))}
                    </div>
                  )}
                  {!r.equal && r.type === 'multi' && (
                    <div className="mt-2 space-y-1 text-xs text-gray-700">
                      {r.missing_local.map((d) => <p key={d.id}>➕ fehlt hier: {d.name}</p>)}
                      {r.changed.map((d) => <p key={d.id}>✏️ {d.name}: {d.fields.join(', ')}</p>)}
                      {r.only_local.map((d) => <p key={d.id} className="text-gray-500">ℹ️ nur hier vorhanden (bleibt): {d.name}</p>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {compareResult.report.some((r) => !r.equal) && (
              <Button onClick={handleApply} disabled={applying} className="bg-teal-600 hover:bg-teal-700" data-testid="sync-apply-btn">
                <Check className="h-4 w-4 mr-2" />
                {applying ? 'Übernimmt...' : 'Ausgewählte Bereiche übernehmen'}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
